/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * Before/after polygon comparison for a geofence audit record.
 *
 * A geofence edit is dominated by geometry — a wall of point coordinates that means nothing read as
 * rows. What a reader actually needs is to *see* the old shape against the new one. This renders the
 * two polygons on one map: the previous boundary as a red dashed outline, the new boundary as a
 * solid blue one, each independently hideable.
 *
 * It draws exactly what it is handed and infers nothing. A caller with only loose vertices passes
 * them as `moved` and gets markers, not a shape — see the note on that prop.
 *
 * Self-contained on purpose: unlike `LocationMap` it fetches nothing and owns no app state, so it can
 * be dropped into the detail panel without pulling the geofence API in behind it.
 */

import React, { useEffect, useRef, useState } from 'react'

import { Map, View, Feature } from 'ol'
import { createEmpty, extend, isEmpty } from 'ol/extent'
import { Point, Polygon } from 'ol/geom'
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer'
import { fromLonLat } from 'ol/proj'
import { OSM, Vector as VectorSource } from 'ol/source'
import XYZ from 'ol/source/XYZ'
import { Style, Fill, Stroke, Circle as CircleStyle, Text } from 'ol/style'
import { FiEye, FiEyeOff } from 'react-icons/fi'

import 'ol/ol.css'

export interface LatLng {
  lat: number
  lng: number
}

/**
 * One vertex the edit moved, with the position it held before and the one it holds now.
 *
 * Used when the record carries no boundary snapshot to draw a polygon from — see `moved` below.
 */
export interface MovedPoint {
  /** The point's index in the boundary, shown on the marker so two moves can be told apart. */
  index: number
  before?: LatLng
  after?: LatLng
}

interface GeofenceDiffMapProps {
  /** The boundary before the edit. Empty when the shape was newly created. */
  before: LatLng[]
  /** The boundary after the edit. Empty when the shape was deleted. */
  after: LatLng[]
  /** Legend text for the `before` shape, e.g. "Values when deleted" on a deletion. */
  beforeLabel?: string
  /** Legend text for the `after` shape. */
  afterLabel?: string
  /**
   * Individual vertices that moved, drawn as unconnected markers in the tone of the side they
   * belong to.
   *
   * The audit diff only restates the points an edit actually touched, so dragging one corner of an
   * eight-point fence yields one point — not a shape. Rather than showing nothing, we plot the
   * vertices we do have.
   *
   * Deliberately unlinked: a line from a vertex's old position to its new one is a claim about the
   * outline, and without the rest of the boundary there is nothing to back it. Markers state only
   * what the record actually says — this point was here, now it is there.
   */
  moved?: MovedPoint[]
}

const BEFORE_COLOR = '#dc2626'
const AFTER_COLOR = '#2563eb'

/** A stable default, so an omitted `moved` prop does not rebuild the map on every render. */
const NO_MOVED: MovedPoint[] = []

const polygonFeature = (points: LatLng[], color: string, dashed: boolean): Feature => {
  const ring = points.map((p) => fromLonLat([p.lng, p.lat]))
  // Close the ring so the outline reads as a shape rather than an open path.
  if (ring.length > 0) ring.push(ring[0])

  const feature = new Feature({ geometry: new Polygon([ring]) })
  feature.setStyle(
    new Style({
      stroke: new Stroke({
        color,
        width: 2.5,
        lineDash: dashed ? [8, 6] : undefined,
      }),
      fill: new Fill({ color: `${color}1a` }),
    })
  )
  return feature
}

/** A numbered dot for a single vertex, filled in the tone of the side it belongs to. */
const markerFeature = (point: LatLng, color: string, index: number): Feature => {
  const feature = new Feature({ geometry: new Point(fromLonLat([point.lng, point.lat])) })
  feature.setStyle(
    new Style({
      image: new CircleStyle({
        radius: 7,
        fill: new Fill({ color }),
        stroke: new Stroke({ color: '#ffffff', width: 2 }),
      }),
      text: new Text({
        text: String(index + 1),
        offsetY: -14,
        font: '600 11px system-ui, sans-serif',
        fill: new Fill({ color }),
        stroke: new Stroke({ color: '#ffffff', width: 3 }),
      }),
    })
  )
  return feature
}

/**
 * A single map showing the previous and current geofence boundaries together.
 *
 * `before` and `after` are drawn as closed shapes, so a caller must only pass a complete boundary.
 * Partial geometry belongs in `moved`, which draws points and nothing joining them.
 */
export const GeofenceDiffMap: React.FC<GeofenceDiffMapProps> = ({
  before,
  after,
  moved = NO_MOVED,
  beforeLabel = 'Before',
  afterLabel = 'After',
}) => {
  const hasBefore = before.length > 0 || moved.some((point) => point.before)
  const hasAfter = after.length > 0 || moved.some((point) => point.after)

  const [showBefore, setShowBefore] = useState(true)
  const [showAfter, setShowAfter] = useState(true)
  const [useSatellite, setUseSatellite] = useState(false)

  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<Map | null>(null)
  const beforeLayerRef = useRef<VectorLayer<VectorSource> | null>(null)
  const afterLayerRef = useRef<VectorLayer<VectorSource> | null>(null)

  useEffect(() => {
    if (!mapRef.current) return

    const osmLayer = new TileLayer({ source: new OSM({ transition: 0 }), visible: !useSatellite })
    const satelliteLayer = new TileLayer({
      source: new XYZ({
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        attributions:
          'Tiles © Esri — Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
        maxZoom: 19,
      }),
      visible: useSatellite,
    })

    const beforeFeatures = before.length > 0 ? [polygonFeature(before, BEFORE_COLOR, true)] : []
    const afterFeatures = after.length > 0 ? [polygonFeature(after, AFTER_COLOR, false)] : []

    // Moved vertices ride along on the layer for the side they belong to, so the eye toggles keep
    // meaning the same thing whether the map is showing shapes, markers, or both.
    for (const point of moved) {
      if (point.before) beforeFeatures.push(markerFeature(point.before, BEFORE_COLOR, point.index))
      if (point.after) afterFeatures.push(markerFeature(point.after, AFTER_COLOR, point.index))
    }

    const beforeLayer = new VectorLayer({
      source: new VectorSource({ features: beforeFeatures }),
      visible: showBefore,
    })
    const afterLayer = new VectorLayer({
      source: new VectorSource({ features: afterFeatures }),
      visible: showAfter,
    })
    beforeLayer.setZIndex(1)
    afterLayer.setZIndex(2)
    beforeLayerRef.current = beforeLayer
    afterLayerRef.current = afterLayer

    const map = new Map({
      target: mapRef.current,
      layers: [osmLayer, satelliteLayer, beforeLayer, afterLayer],
      view: new View({ center: fromLonLat([0, 0]), zoom: 2, constrainResolution: true }),
      controls: [],
    })
    mapInstanceRef.current = map

    // Fit to everything we drew, before and after together, so both shapes are visible at once.
    const extent = createEmpty()
    for (const layer of [beforeLayer, afterLayer]) {
      const source = layer.getSource()
      if (source && source.getFeatures().length > 0) extend(extent, source.getExtent())
    }
    if (!isEmpty(extent)) {
      map.getView().fit(extent, { padding: [32, 32, 32, 32], maxZoom: 17 })
    }

    return () => {
      map.setTarget(undefined)
      mapInstanceRef.current = null
      beforeLayerRef.current = null
      afterLayerRef.current = null
    }
    // Rebuilding on satellite toggle keeps the effect simple; the shapes are cheap to redraw.
  }, [before, after, moved, useSatellite])

  useEffect(() => {
    beforeLayerRef.current?.setVisible(showBefore)
  }, [showBefore])

  useEffect(() => {
    afterLayerRef.current?.setVisible(showAfter)
  }, [showAfter])

  return (
    <div className="relative overflow-hidden rounded-box border border-base-300">
      <div ref={mapRef} className="h-[320px] w-full" />

      {/* Layer toggles: eye affordance so colour is never the only signal for which shape is which. */}
      <div className="absolute right-2 top-2 z-10 flex flex-col gap-1">
        {hasBefore && (
          <button
            type="button"
            onClick={() => setShowBefore((v) => !v)}
            aria-pressed={showBefore}
            className="flex items-center gap-1.5 rounded-field border border-base-300 bg-base-100/95 px-2 py-1 text-xs font-medium shadow-sm hover:bg-base-200"
          >
            {showBefore ? <FiEye aria-hidden /> : <FiEyeOff aria-hidden className="opacity-50" />}
            <span
              aria-hidden
              className="inline-block h-0 w-4 border-t-2 border-dashed"
              style={{ borderColor: BEFORE_COLOR }}
            />
            {beforeLabel}
          </button>
        )}
        {hasAfter && (
          <button
            type="button"
            onClick={() => setShowAfter((v) => !v)}
            aria-pressed={showAfter}
            className="flex items-center gap-1.5 rounded-field border border-base-300 bg-base-100/95 px-2 py-1 text-xs font-medium shadow-sm hover:bg-base-200"
          >
            {showAfter ? <FiEye aria-hidden /> : <FiEyeOff aria-hidden className="opacity-50" />}
            <span aria-hidden className="inline-block h-0 w-4 border-t-2" style={{ borderColor: AFTER_COLOR }} />
            {afterLabel}
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={() => setUseSatellite((v) => !v)}
        className="absolute bottom-2 left-2 z-10 rounded-field border border-base-300 bg-base-100/95 px-2 py-1 text-xs font-medium shadow-sm hover:bg-base-200"
      >
        {useSatellite ? 'Map' : 'Satellite'}
      </button>
    </div>
  )
}
